import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const ProductRemovalImpactChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, events, currencySettings, isLoading } = useBoardChartData();
    const currencySymbol = getCurrencySymbol(currencySettings?.default);

    const chartData = useMemo(() => {
        if (!events || !finance?.dailyBreakdown) return [];

        const removals = events.filter((e: any) =>
            e.category === 'product' && ['unpublished', 'destroyed'].includes((e.type || e.eventType) as string)
        );
        if (removals.length === 0) return [];

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();
        const results: { name: string; before: number; after: number }[] = [];

        removals.slice(0, 8).forEach((event: any) => {
            const eventDate = event.date?.split('T')[0];
            if (!eventDate) return;
            const dateIdx = sortedDates.indexOf(eventDate);

            const beforeDays = sortedDates.slice(Math.max(0, dateIdx - 7), dateIdx);
            const afterDays = sortedDates.slice(dateIdx, dateIdx + 7);

            const avgBefore = beforeDays.length > 0
                ? beforeDays.reduce((s, d) => s + (finance.dailyBreakdown![d]?.metrics.revenue || 0), 0) / beforeDays.length : 0;
            const avgAfter = afterDays.length > 0
                ? afterDays.reduce((s, d) => s + (finance.dailyBreakdown![d]?.metrics.revenue || 0), 0) / afterDays.length : 0;

            const label = event.description || format(parseISO(eventDate), 'MMM dd');
            results.push({ name: label.length > 20 ? label.substring(0, 20) + '...' : label, before: avgBefore, after: avgAfter });
        });

        return results;
    }, [events, finance]);

    const isEmpty = !isLoading && chartData.length === 0;
    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <CartesianGrid strokeDasharray={CHART_STYLES.grid.strokeDasharray} vertical={false} stroke={CHART_STYLES.grid.stroke} opacity={CHART_STYLES.grid.opacity} />
                        <XAxis dataKey="name" {...CHART_STYLES.axis} tick={{ ...CHART_STYLES.axis.tick, fontSize: 9 }} />
                        <YAxis {...CHART_STYLES.axis} tickFormatter={(v) => formatCompactNumber(v, currencySymbol)} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} width={55} />
                        <Tooltip contentStyle={CHART_STYLES.tooltip.contentStyle} formatter={(v: number) => formatCurrency(v, currencySymbol)} />
                        <Legend wrapperStyle={{ fontSize: '10px' }} />
                        <Bar dataKey="before" name={t('charts.before', 'Before Removal') as string} fill={CHART_COLORS.revenue} radius={[3, 3, 0, 0]} maxBarSize={30} />
                        <Bar dataKey="after" name={t('charts.after', 'After Removal') as string} fill={CHART_COLORS.danger} radius={[3, 3, 0, 0]} maxBarSize={30} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
